import { useEffect, useState } from 'react'

import { fetchSrqStatus } from 'services/CourseService'

export type SrqStatusState = {
  srqEnabled: boolean
  quizzesDueToday: number
  srqRedDotsVisible: boolean
}

const DEFAULT_SRQ_STATUS: SrqStatusState = {
  srqEnabled: false,
  quizzesDueToday: 0,
  srqRedDotsVisible: true
}

let sharedSrqStatus: SrqStatusState = { ...DEFAULT_SRQ_STATUS }
const srqStatusListeners = new Set<(state: SrqStatusState) => void>()
let srqStatusRequest: {
  courseId: string
  promise: Promise<SrqStatusState>
} | null = null

export const getSharedSrqStatus = (): SrqStatusState => sharedSrqStatus

export const setSharedSrqStatus = (state: SrqStatusState) => {
  sharedSrqStatus = state
  srqStatusListeners.forEach(listener => listener(state))
}

export const patchSharedSrqStatus = (partial: Partial<SrqStatusState>) => {
  setSharedSrqStatus({
    ...sharedSrqStatus,
    ...partial
  })
}

export const subscribeSrqStatus = (
  listener: (state: SrqStatusState) => void
): (() => void) => {
  srqStatusListeners.add(listener)
  return () => {
    srqStatusListeners.delete(listener)
  }
}

export const clearSrqStatusRequest = () => {
  srqStatusRequest = null
}

export const loadSrqStatus = (courseId: string): Promise<SrqStatusState> => {
  if (srqStatusRequest?.courseId === courseId) {
    return srqStatusRequest.promise
  }

  const promise = fetchSrqStatus(courseId)
    .then(response => {
      const nextState: SrqStatusState = {
        srqEnabled: response?.data?.srq_enabled === true,
        quizzesDueToday: response?.data?.quizzes_due_today ?? 0,
        srqRedDotsVisible: response?.data?.srq_red_dots_visible ?? true
      }
      setSharedSrqStatus(nextState)
      return nextState
    })
    .catch(() => {
      const fallbackState: SrqStatusState = {
        srqEnabled: false,
        quizzesDueToday: 0,
        srqRedDotsVisible: true
      }
      setSharedSrqStatus(fallbackState)
      return fallbackState
    })
    .finally(() => {
      if (srqStatusRequest?.courseId === courseId) {
        srqStatusRequest = null
      }
    })

  srqStatusRequest = { courseId, promise }
  return promise
}

export const resetSharedSrqStatus = () => {
  setSharedSrqStatus({ ...DEFAULT_SRQ_STATUS })
  clearSrqStatusRequest()
}

/** Subscribe to shared course SRQ status (enabled, due count, red-dot visibility). */
export const useSrqStatus = (): SrqStatusState => {
  const [status, setStatus] = useState<SrqStatusState>(sharedSrqStatus)

  useEffect(() => subscribeSrqStatus(setStatus), [])

  return status
}
